import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { FaqAnswer } from './FaqAnswer';
import { FAQ_LAYOUT_CLASSES, type FaqLayout } from './faqLayout';
import type { FaqQA, FaqSection } from './faqTypes';

type FaqSectionViewProps = {
  section: FaqSection;
  /**
   * [MCV-1592] Level for this section's title. Defaults to 2, which is right
   * for a section that stands on its own. Sections rendered UNDER a group label
   * pass 3, so the accessible outline reflects the grouping the page shows
   * visually -- otherwise a group and its own sections are siblings.
   */
  headingLevel?: 2 | 3;
  /** [MCV-1724] Which scroll geometry the section's anchor clearance follows. Defaults to the public site. */
  layout?: FaqLayout;
};

type FaqQaGroup = {
  heading?: string;
  qas: FaqQA[];
};

// Splits a flat QA list into groups: a QA with a `heading` starts a new group
// that collects the following QAs until the next heading. Sections without
// headings yield a single heading-less group.
function groupQas(qas: FaqQA[]): FaqQaGroup[] {
  const groups: FaqQaGroup[] = [];
  for (const qa of qas) {
    const currentGroup = groups[groups.length - 1];
    if (qa.heading || !currentGroup) {
      groups.push({ heading: qa.heading, qas: [qa] });
    } else {
      currentGroup.qas.push(qa);
    }
  }
  return groups;
}

// One FAQ section: an anchorable heading, optional intro prose, and a list of
// collapsible question/answer disclosures (optionally grouped under subheadings).
export const FaqSectionView = ({
  section,
  headingLevel = 2,
  layout = 'public',
}: FaqSectionViewProps) => {
  // [MCV-1514] Anchor clearance has two obstructions to clear on mobile — the
  // 80px fixed header plus the ~67px sticky "Jump to" nav below it — but only
  // the header on `lg`, where the nav is a sidebar beside the content rather
  // than over it. [MCV-1724] Inside the app there is no header over the content,
  // so the `app` layout clears only the "Jump to" bar on mobile and the sticky
  // offset on `lg` (see faqLayout.ts).
  // Derived, not branched: the className strings carry the full type scale, so
  // the level changes without anything moving visually.
  const grouped = headingLevel === 3;
  const SectionHeading = grouped ? 'h3' : 'h2';
  const SubHeading = grouped ? 'h4' : 'h3';

  // An answer's `h` block must sit one below its ACTUAL parent heading, which
  // is not always SubHeading: intro and outro render before/after every group,
  // and a QA group with no `heading` emits no SubHeading at all. Using the
  // SubHeading level unconditionally skips a level in those three cases -- a
  // pre-existing bug that survived because HLX has no `h` blocks anywhere, so
  // an HLX-only outline test can never see it. MCV has them in 30 sections.
  const underSection: 3 | 4 = grouped ? 4 : 3;
  const underSubHeading: 4 | 5 = grouped ? 5 : 4;

  return (
    <section
      id={section.id}
      data-cy={`faq-section-${section.id}`}
      className={`${FAQ_LAYOUT_CLASSES[layout].sectionScrollMargin} border-t border-grey-800 pt-10 first:border-t-0 first:pt-0`}
    >
      <SectionHeading className="text-2xl font-semibold tracking-tight text-grey-100 sm:text-3xl">
        {section.title}
      </SectionHeading>

      {section.intro && section.intro.length > 0 && (
        <div className="mt-4 max-w-3xl">
          <FaqAnswer blocks={section.intro} headingLevel={underSection} />
        </div>
      )}

      <div className="mt-6 space-y-8">
        {groupQas(section.qas).map((group, groupIndex) => (
          <div key={group.heading ?? groupIndex}>
            {group.heading && (
              <SubHeading className="mb-3 text-lg font-semibold text-grey-100">
                {group.heading}
              </SubHeading>
            )}
            <dl className="divide-y divide-grey-800">
              {group.qas.map((qa) => (
                <Disclosure key={qa.question} as="div" className="py-5 first:pt-0">
                  <dt>
                    <DisclosureButton className="group flex w-full items-start justify-between gap-6 text-left text-grey-100">
                      <span className="text-[1rem] font-semibold leading-7">{qa.question}</span>
                      <span className="flex h-7 shrink-0 items-center text-primary-50">
                        <PlusIcon aria-hidden="true" className="size-5 group-data-open:hidden" />
                        <MinusIcon
                          aria-hidden="true"
                          className="size-5 group-not-data-open:hidden"
                        />
                      </span>
                    </DisclosureButton>
                  </dt>
                  <DisclosurePanel as="dd" className="mt-3 pr-8">
                    <FaqAnswer
                      blocks={qa.answer}
                      headingLevel={group.heading ? underSubHeading : underSection}
                    />
                  </DisclosurePanel>
                </Disclosure>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {/* [MCV-1501] The source document closes most sections with a "Closing
          Thought". It belongs after the questions, and it must sit outside the
          disclosures — folded into the last answer it would only be readable
          by whoever happened to expand that one question. */}
      {section.outro && section.outro.length > 0 && (
        <div className="mt-8 max-w-3xl border-t border-grey-800 pt-6">
          <FaqAnswer blocks={section.outro} headingLevel={underSection} />
        </div>
      )}
    </section>
  );
};
